import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, Loader2, Send } from "lucide-react";
import { api, brl, CATEGORIES, errMsg } from "@/lib/api";
import { Field, inputCls } from "@/components/landing/ProposalForm";
import { MethodPicker } from "@/components/gallery/CheckoutDialog";

const EMPTY = { name: "", email: "", phone: "", event_type: "Social", date: "", location: "", guests: "", message: "" };

export const HireForm = ({ photographer, mpEnabled }) => {
  const canBook = photographer.booking_fee_cents > 0;
  const [mode, setMode] = useState(canBook ? "booking" : "quote");
  const [f, setF] = useState(EMPTY);
  const [method, setMethod] = useState("pix");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "quote") {
        await api.post("/quotes", { ...f, photographer_id: photographer.id });
        toast.success("Pedido de orçamento enviado! O fotógrafo vai responder em breve.");
        setF(EMPTY);
      } else {
        const { guests, ...rest } = f;
        const { data } = await api.post("/bookings", { ...rest, method, photographer_id: photographer.id });
        if (data?.init_point) window.location.assign(data.init_point);
        else nav(`/pedido/${data.id}?t=${data.token}`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-[1.75rem] border border-white/10 bg-ink-2 p-6 sm:p-8" data-testid="hire-form-card">
      <div className="flex gap-1 rounded-full border border-white/10 bg-ink p-1">
        {[["quote", "Pedir orçamento"], ...(canBook ? [["booking", "Reservar data"]] : [])].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setMode(k)} data-testid={`hire-mode-${k}`}
            className={`flex-1 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors duration-300 ${mode === k ? "bg-flash text-ink" : "text-haze hover:text-foreground"}`}>{l}</button>
        ))}
      </div>
      {mode === "booking" && <p className="mt-5 text-sm text-haze">Sinal de reserva: <b className="text-flash" data-testid="hire-booking-fee">{brl(photographer.booking_fee_cents)}</b> — garante a data na agenda.</p>}
      <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="hire-form">
        <Field label="Nome"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="hire-name-input" /></Field>
        <Field label="WhatsApp"><input required value={f.phone} onChange={set("phone")} className={inputCls} data-testid="hire-phone-input" /></Field>
        <Field label="E-mail" className="sm:col-span-2"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="hire-email-input" /></Field>
        <Field label="Tipo de evento">
          <select value={f.event_type} onChange={set("event_type")} className={inputCls} data-testid="hire-type-select">
            {CATEGORIES.map((c) => <option key={c.id} className="bg-ink">{c.label}</option>)}
          </select>
        </Field>
        <Field label="Data"><input required={mode === "booking"} type="date" min={new Date().toISOString().slice(0, 10)} value={f.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} data-testid="hire-date-input" /></Field>
        <Field label="Local" className={mode === "quote" ? "" : "sm:col-span-2"}><input value={f.location} onChange={set("location")} className={inputCls} data-testid="hire-location-input" /></Field>
        {mode === "quote" && <Field label="Convidados"><input value={f.guests} onChange={set("guests")} className={inputCls} data-testid="hire-guests-input" /></Field>}
        <Field label="Detalhes" className="sm:col-span-2"><textarea rows={3} value={f.message} onChange={set("message")} className={inputCls} data-testid="hire-message-input" /></Field>
        {mode === "booking" && <div className="sm:col-span-2"><MethodPicker method={method} setMethod={setMethod} mpEnabled={mpEnabled} /></div>}
        <button disabled={busy} data-testid="hire-submit-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-flash px-6 py-4 font-semibold text-ink hover:bg-flash-2 disabled:opacity-60 sm:col-span-2">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "quote" ? <Send className="h-5 w-5" /> : <CalendarCheck className="h-5 w-5" />}
          {mode === "quote" ? "Enviar pedido de orçamento" : "Reservar e pagar sinal"}
        </button>
      </form>
    </div>
  );
};
